import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import { updateCandidateProfile, parseResumeWithAI, getCandidateProfile } from '../services/api';
import SessionManager from '../utils/sessionManager';
import SearchableMultiSelect from '../components/SearchableMultiSelect';
import ProfileCompletionModal from '../components/molecules/ProfileCompletionModal';

const ProfileSetupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'manual';
  
  const [currentStep, setCurrentStep] = useState(mode === 'resume' ? 0 : 0); // Always start at index 0 s
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  const [formData, setFormData] = useState({
    // Basic Info
    fullName: '',
    email: '',
    phone: '',
    jobTitle: '', // Added job title fields
    location: '',
    nationality: '',
    nationalId: '',
    gender: '',
    dob: '',
    
    // Education
    education: [{
      institution: '',
      degree: '',
      field: '',
      gpa: '',
      startDate: '',
      endDate: '',
      location: ''
    }],
    
    // Work Experience
    workExperience: [{
      company: '',
      position: '',
      startDate: '',
      endDate: '',
      current: false,
      location: '',
      description: ''
    }],
    
    // Projects
    projects: [{
      title: '',
      description: '',
      technologies: '',
      link: '',
      startDate: '',
      endDate: ''
    }],
    
    // Skills & Languages
    skills: [],
    languages: [],
    
    // Courses
    courses: [{
      name: '',
      provider: '',
      completionDate: '',
      certificateUrl: ''
    }],
    
    // Additional
    hobbies: '',
    linkedin: '',
    github: '',
    portfolio: ''
  });

  const steps = [
    ...(mode === 'resume' ? [{ id: 0, title: 'Upload Resume', icon: 'upload_file' }] : []),
    { id: 1, title: 'Basic Info', icon: 'person' },
    { id: 2, title: 'Education', icon: 'school' },
    { id: 3, title: 'Work Experience', icon: 'work' },
    { id: 4, title: 'Projects', icon: 'code' },
    { id: 5, title: 'Skills & Languages', icon: 'star' },
    { id: 6, title: 'Courses', icon: 'school' },
    { id: 'resume', title: 'Upload Resume', icon: 'upload_file' }, // Resume upload for manual mode
    { id: 7, title: 'Preview', icon: 'visibility' }
  ];

  useEffect(() => {
    const loadExistingProfile = async (candidateSession) => {
      try {
        const candidateId = candidateSession?.user_id;
        const loginEmail = candidateSession?.email || '';
        
        if (!candidateId && !loginEmail) {
          return;
        }
        
        const profile = await getCandidateProfile(candidateId, loginEmail);
        
        if (profile) {
          console.log('Loading existing profile data:', profile);
          
          // Map profile data to form structure
          setFormData(prev => ({
            ...prev,
            fullName: profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
            email: loginEmail,
            phone: profile.phone_number || profile.phone || '',
            jobTitle: profile.job_role || '', // Load job title from profile
            location: profile.location || '',
            nationality: profile.nationality || '',
            nationalId: profile.national_id || '',
            gender: profile.gender || '',
            dob: profile.dob || '',
            education: Array.isArray(profile.education) && profile.education.length > 0 
              ? profile.education 
              : prev.education,
            workExperience: Array.isArray(profile.work_experience) && profile.work_experience.length > 0 
              ? profile.work_experience 
              : prev.workExperience,
            projects: Array.isArray(profile.projects) && profile.projects.length > 0 
              ? profile.projects 
              : prev.projects,
            skills: Array.isArray(profile.skills) ? profile.skills : prev.skills,
            languages: Array.isArray(profile.languages) ? profile.languages : prev.languages,
            courses: Array.isArray(profile.courses) && profile.courses.length > 0 
              ? profile.courses 
              : prev.courses,
            hobbies: profile.hobbies || '',
            linkedin: profile.linkedin || '',
            github: profile.github || '',
            portfolio: profile.portfolio || ''
          }));
          
          console.log('Profile data loaded successfully');
        }
      } catch (error) {
        console.error('Error loading existing profile:', error);
        // Don't show error to user, just continue with empty form
      }
    };

    // Get candidate email from session and ensure it's always set
    const candidateSession = SessionManager.getSession('candidate');
    if (candidateSession?.email) {
      setFormData(prev => ({ 
        ...prev, 
        email: candidateSession.email 
      }));
      
      // Load existing profile data if available
      loadExistingProfile(candidateSession);
    } else {
      // If no email in session, redirect to login
      console.error('No email found in session');
      navigate('/login');
    }
  }, [navigate]);

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setResumeFile(file);
    setIsParsing(true);

    try {
      const response = await parseResumeWithAI(file);
      console.log('Resume parsing response:', response);
      
      // Extract data from response (API returns { success, data, raw_text })
      const parsedData = response.data || response;
      
      // Map parsed data to form structure with comprehensive extraction
      setFormData(prev => ({
        ...prev,
        // Personal Info
        fullName: parsedData.personalInfo?.name || prev.fullName,
        phone: parsedData.personalInfo?.phone || prev.phone,
        location: parsedData.personalInfo?.location || prev.location,
        nationality: parsedData.personalInfo?.nationality || prev.nationality,
        nationalId: parsedData.personalInfo?.nationalId || prev.nationalId,
        gender: parsedData.personalInfo?.gender || prev.gender,
        dob: parsedData.personalInfo?.dob || prev.dob,
        
        // Education - map all fields properly
        education: parsedData.education?.length > 0 
          ? parsedData.education.map(edu => ({
              institution: edu.institution || '',
              institutionOther: '',
              degree: edu.degree || '',
              degreeOther: '',
              field: edu.field || '',
              fieldOther: '',
              gpa: edu.gpa || '',
              startDate: edu.startDate || '',
              endDate: edu.endDate || '',
              location: edu.location || ''
            }))
          : prev.education,
        
        // Work Experience - map all fields properly
        workExperience: parsedData.workExperience?.length > 0 
          ? parsedData.workExperience.map(work => ({
              company: work.company || '',
              position: work.position || '',
              startDate: work.startDate || '',
              endDate: work.endDate || '',
              current: work.current || false,
              location: work.location || '',
              description: work.description || ''
            }))
          : prev.workExperience,
        
        // Projects - map all fields properly
        projects: parsedData.projects?.length > 0 
          ? parsedData.projects.map(proj => ({
              title: proj.title || '',
              description: proj.description || '',
              technologies: proj.technologies || '',
              link: proj.link || '',
              startDate: proj.startDate || '',
              endDate: proj.endDate || ''
            }))
          : prev.projects,
        
        // Skills - ensure it's an array
        skills: Array.isArray(parsedData.skills) && parsedData.skills.length > 0 
          ? parsedData.skills 
          : prev.skills,
        
        // Languages - ensure it's an array
        languages: Array.isArray(parsedData.languages) && parsedData.languages.length > 0 
          ? parsedData.languages 
          : prev.languages,
        
        // Certifications/Courses - map all fields properly
        courses: parsedData.certifications?.length > 0 
          ? parsedData.certifications.map(cert => ({
              name: cert.name || '',
              provider: cert.issuer || cert.provider || '',
              completionDate: cert.date || cert.completionDate || '',
              certificateUrl: cert.certificateUrl || ''
            }))
          : prev.courses,
        
        // Hobbies - convert array to comma-separated string
        hobbies: Array.isArray(parsedData.hobbies) && parsedData.hobbies.length > 0
          ? parsedData.hobbies.join(', ')
          : prev.hobbies,
        
        // Social Links
        linkedin: parsedData.links?.linkedin || prev.linkedin,
        github: parsedData.links?.github || prev.github,
        portfolio: parsedData.links?.portfolio || prev.portfolio
      }));

      console.log('✅ Resume parsed successfully! Data extracted and filled into form.');
      alert('Resume parsed successfully! Please review and edit the extracted information.');
      setCurrentStep(1); // Move to Basic Info step
    } catch (error) {
      console.error('Error parsing resume:', error);
      alert(`Failed to parse resume: ${error.message || 'Please try again or enter manually.'}`);
    } finally {
      setIsParsing(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayFieldChange = (arrayName, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addArrayItem = (arrayName, template) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], template]
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > (mode === 'resume' ? 0 : 1)) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const candidateSession = SessionManager.getSession('candidate');
      const candidateId = candidateSession?.user_id;
      const email = formData.email || candidateSession?.email;
      
      // Validate required fields
      if (!email) {
        alert('Email is required. Please log in again.');
        navigate('/login');
        return;
      }
      
      if (!formData.fullName || !formData.phone) {
        alert('Please fill in all required fields (Name and Phone).');
        return;
      }
      
      // Create FormData for multipart upload
      const profileData = new FormData();
      profileData.append('candidate_id', candidateId);
      profileData.append('full_name', formData.fullName);
      profileData.append('email', email);
      profileData.append('phone', formData.phone);
      profileData.append('job_role', formData.jobTitle || ''); // Add job title
      profileData.append('location', formData.location || '');
      profileData.append('nationality', formData.nationality || '');
      profileData.append('national_id', formData.nationalId || '');
      profileData.append('gender', formData.gender || '');
      profileData.append('dob', formData.dob || '');
      profileData.append('marital_status', formData.maritalStatus || '');
      profileData.append('hometown', formData.hometown || '');
      profileData.append('education', JSON.stringify(formData.education));
      profileData.append('work_experience', JSON.stringify(formData.workExperience));
      profileData.append('projects', JSON.stringify(formData.projects));
      profileData.append('skills', JSON.stringify(formData.skills));
      profileData.append('languages', JSON.stringify(formData.languages));
      profileData.append('courses', JSON.stringify(formData.courses));
      profileData.append('hobbies', formData.hobbies || '');
      profileData.append('linkedin', formData.linkedin || '');
      profileData.append('github', formData.github || '');
      profileData.append('portfolio', formData.portfolio || '');
      
      if (resumeFile) {
        profileData.append('resume', resumeFile);
      }
      
      // Call API directly with fetch instead of using the helper function
      const currentSession = SessionManager.getSession('candidate');
      const token = currentSession?.access_token;
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';
      
      const response = await fetch(`${API_BASE_URL}/candidates/profile`, {
        method: 'PUT',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: profileData
      });
      
      const responseData = await response.json().catch(() => ({}));
      
      if (!response.ok) {
        const message = responseData?.detail || 'Failed to save profile';
        throw new Error(message);
      }
      
      // Update session with new profile data
      const profileUpdateData = {
        full_name: formData.fullName,
        first_name: formData.fullName.split(' ')[0],
        last_name: formData.fullName.split(' ').slice(1).join(' '),
        phone_number: formData.phone,
        job_role: formData.jobTitle, // Include job title in session
        location: formData.location,
        nationality: formData.nationality,
        national_id: formData.nationalId,
        gender: formData.gender,
        dob: formData.dob,
        education: formData.education,
        work_experience: formData.workExperience,
        projects: formData.projects,
        skills: formData.skills,
        languages: formData.languages,
        courses: formData.courses,
        hobbies: formData.hobbies,
        linkedin: formData.linkedin,
        github: formData.github,
        portfolio: formData.portfolio
      };
      
      SessionManager.updateSessionProfile('candidate', profileUpdateData);
      console.log('✅ Updated session with new profile data');
      
      // Clear the verification banner dismissed flag so it shows after profile completion
      localStorage.removeItem('verification_banner_dismissed');
      localStorage.removeItem('verification_popup_dismissed_until');
      
      // Set flag to show verification popup immediately on landing page
      sessionStorage.setItem('profile_just_completed', 'true');
      
      // Show professional success modal
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(`Failed to save profile: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    const stepId = steps[currentStep]?.id;
    
    switch (stepId) {
      case 0: // Resume Upload (only in resume mode)
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Upload Your Resume
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                Upload your resume and we'll automatically extract all your information including education, work experience, projects, skills, certifications, and more!
              </p>
            </div>

            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-12 text-center hover:border-primary transition-colors">
              <input
                type="file"
                id="resume-upload"
                accept=".pdf,.doc,.docx"
                onChange={handleResumeUpload}
                className="hidden"
                disabled={isParsing}
              />
              <label htmlFor="resume-upload" className="cursor-pointer">
                <Icon name="upload_file" size={64} className="text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {resumeFile ? resumeFile.name : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  PDF, DOC, or DOCX (max 5MB)
                </p>
              </label>
            </div>

            {isParsing && (
              <div className="text-center bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Analyzing your resume with AI...
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Extracting personal info, education, work experience, projects, skills, certifications, and more
                </p>
              </div>
            )}
            
            {resumeFile && !isParsing && (
              <div className="text-center bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <Icon name="check_circle" size={24} className="text-green-500 mx-auto mb-2" />
                <p className="text-sm text-green-700 dark:text-green-300">
                  Resume uploaded successfully! Click Next to review extracted information.
                </p>
              </div>
            )}
          </div>
        );

      case 1: // Basic Info
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Basic Information
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  required
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Job Title / Role
                </label>
                <input
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                  placeholder="e.g., Software Engineer, Data Analyst"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="City, Country"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nationality
                </label>
                <input
                  type="text"
                  value={formData.nationality}
                  onChange={(e) => handleInputChange('nationality', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  National ID
                </label>
                <input
                  type="text"
                  value={formData.nationalId}
                  onChange={(e) => handleInputChange('nationalId', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Gender
                </label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleInputChange('dob', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>
        );

      case 2: // Education
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Education
              </h2>
              <Button
                onClick={() => addArrayItem('education', {
                  institution: '',
                  institutionOther: '',
                  degree: '',
                  degreeOther: '',
                  field: '',
                  fieldOther: '',
                  gpa: '',
                  startDate: '',
                  endDate: '',
                  location: ''
                })}
                variant="outline"
                size="sm"
              >
                <Icon name="add" size={20} className="mr-1" />
                Add Education
              </Button>
            </div>

            {formData.education.map((edu, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg relative">
                {formData.education.length > 1 && (
                  <button
                    onClick={() => removeArrayItem('education', index)}
                    className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                  >
                    <Icon name="delete" size={20} />
                  </button>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Institution *
                    </label>
                    <select
                      value={edu.institution}
                      onChange={(e) => handleArrayFieldChange('education', index, 'institution', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                      required
                    >
                      <option value="">Select Institution</option>
                      <optgroup label="IITs">
                        <option value="IIT Madras">IIT Madras</option>
                        <option value="IIT Delhi">IIT Delhi</option>
                        <option value="IIT Bombay">IIT Bombay</option>
                        <option value="IIT Kanpur">IIT Kanpur</option>
                        <option value="IIT Kharagpur">IIT Kharagpur</option>
                        <option value="IIT Roorkee">IIT Roorkee</option>
                        <option value="IIT Guwahati">IIT Guwahati</option>
                        <option value="IIT Hyderabad">IIT Hyderabad</option>
                      </optgroup>
                      <optgroup label="NITs">
                        <option value="NIT Trichy">NIT Trichy</option>
                        <option value="NIT Surathkal">NIT Surathkal</option>
                        <option value="NIT Warangal">NIT Warangal</option>
                        <option value="NIT Calicut">NIT Calicut</option>
                        <option value="NIT Rourkela">NIT Rourkela</option>
                      </optgroup>
                      <optgroup label="IIITs">
                        <option value="IIIT Hyderabad">IIIT Hyderabad</option>
                        <option value="IIIT Bangalore">IIIT Bangalore</option>
                        <option value="IIIT Delhi">IIIT Delhi</option>
                      </optgroup>
                      <optgroup label="Universities">
                        <option value="Anna University">Anna University</option>
                        <option value="Delhi University">Delhi University</option>
                        <option value="Mumbai University">Mumbai University</option>
                        <option value="Pune University">Pune University</option>
                        <option value="VTU Belgaum">VTU Belgaum</option>
                        <option value="Osmania University">Osmania University</option>
                        <option value="Jadavpur University">Jadavpur University</option>
                      </optgroup>
                      <optgroup label="Private Colleges">
                        <option value="BITS Pilani">BITS Pilani</option>
                        <option value="VIT Vellore">VIT Vellore</option>
                        <option value="Manipal Institute of Technology">Manipal Institute of Technology</option>
                        <option value="SRM University">SRM University</option>
                        <option value="Amity University">Amity University</option>
                        <option value="Sri Sairam Engineering College">Sri Sairam Engineering College</option>
                      </optgroup>
                      <option value="Other">Other (Specify below)</option>
                    </select>
                    {edu.institution === 'Other' && (
                      <input
                        type="text"
                        value={edu.institutionOther || ''}
                        onChange={(e) => handleArrayFieldChange('education', index, 'institutionOther', e.target.value)}
                        placeholder="Enter institution name"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white mt-2"
                        required
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Degree *
                    </label>
                    <select
                      value={edu.degree}
                      onChange={(e) => handleArrayFieldChange('education', index, 'degree', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                      required
                    >
                      <option value="">Select Degree</option>
                      <optgroup label="Undergraduate">
                        <option value="B.Tech">B.Tech (Bachelor of Technology)</option>
                        <option value="B.E.">B.E. (Bachelor of Engineering)</option>
                        <option value="B.Sc">B.Sc (Bachelor of Science)</option>
                        <option value="BCA">BCA (Bachelor of Computer Applications)</option>
                        <option value="B.Com">B.Com (Bachelor of Commerce)</option>
                        <option value="BBA">BBA (Bachelor of Business Administration)</option>
                        <option value="BA">BA (Bachelor of Arts)</option>
                      </optgroup>
                      <optgroup label="Postgraduate">
                        <option value="M.Tech">M.Tech (Master of Technology)</option>
                        <option value="M.E.">M.E. (Master of Engineering)</option>
                        <option value="M.Sc">M.Sc (Master of Science)</option>
                        <option value="MCA">MCA (Master of Computer Applications)</option>
                        <option value="MBA">MBA (Master of Business Administration)</option>
                        <option value="MA">MA (Master of Arts)</option>
                      </optgroup>
                      <optgroup label="Doctorate">
                        <option value="Ph.D">Ph.D (Doctor of Philosophy)</option>
                      </optgroup>
                      <optgroup label="Diploma">
                        <option value="Diploma">Diploma</option>
                        <option value="Advanced Diploma">Advanced Diploma</option>
                      </optgroup>
                      <option value="Other">Other (Specify below)</option>
                    </select>
                    {edu.degree === 'Other' && (
                      <input
                        type="text"
                        value={edu.degreeOther || ''}
                        onChange={(e) => handleArrayFieldChange('education', index, 'degreeOther', e.target.value)}
                        placeholder="Enter degree name"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white mt-2"
                        required
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Field of Study / Specialization
                    </label>
                    <select
                      value={edu.field}
                      onChange={(e) => handleArrayFieldChange('education', index, 'field', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select Field</option>
                      <optgroup label="Computer Science & IT">
                        <option value="Computer Science">Computer Science</option>
                        <option value="Information Technology">Information Technology</option>
                        <option value="Software Engineering">Software Engineering</option>
                        <option value="Data Science">Data Science</option>
                        <option value="Artificial Intelligence">Artificial Intelligence</option>
                        <option value="Cyber Security">Cyber Security</option>
                      </optgroup>
                      <optgroup label="Engineering">
                        <option value="Mechanical Engineering">Mechanical Engineering</option>
                        <option value="Electrical Engineering">Electrical Engineering</option>
                        <option value="Electronics & Communication">Electronics & Communication</option>
                        <option value="Civil Engineering">Civil Engineering</option>
                        <option value="Chemical Engineering">Chemical Engineering</option>
                        <option value="Aerospace Engineering">Aerospace Engineering</option>
                      </optgroup>
                      <optgroup label="Business & Management">
                        <option value="Business Administration">Business Administration</option>
                        <option value="Finance">Finance</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Human Resources">Human Resources</option>
                        <option value="Operations Management">Operations Management</option>
                      </optgroup>
                      <optgroup label="Science">
                        <option value="Physics">Physics</option>
                        <option value="Chemistry">Chemistry</option>
                        <option value="Mathematics">Mathematics</option>
                        <option value="Biology">Biology</option>
                        <option value="Biotechnology">Biotechnology</option>
                      </optgroup>
                      <optgroup label="Arts & Humanities">
                        <option value="English">English</option>
                        <option value="History">History</option>
                        <option value="Psychology">Psychology</option>
                        <option value="Economics">Economics</option>
                      </optgroup>
                      <option value="Other">Other (Specify below)</option>
                    </select>
                    {edu.field === 'Other' && (
                      <input
                        type="text"
                        value={edu.fieldOther || ''}
                        onChange={(e) => handleArrayFieldChange('education', index, 'fieldOther', e.target.value)}
                        placeholder="Enter field of study"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white mt-2"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      GPA / Percentage
                    </label>
                    <input
                      type="text"
                      value={edu.gpa}
                      onChange={(e) => handleArrayFieldChange('education', index, 'gpa', e.target.value)}
                      placeholder="e.g., 3.8/4.0 or 85%"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={edu.location}
                      onChange={(e) => handleArrayFieldChange('education', index, 'location', e.target.value)}
                      placeholder="City, State"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Start Date
                    </label>
                    <input
                      type="month"
                      value={edu.startDate}
                      onChange={(e) => handleArrayFieldChange('education', index, 'startDate', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      End Date
                    </label>
                    <input
                      type="month"
                      value={edu.endDate}
                      onChange={(e) => handleArrayFieldChange('education', index, 'endDate', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 3: // Work Experience
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Work Experience
              </h2>
              <Button
                onClick={() => addArrayItem('workExperience', {
                  company: '',
                  position: '',
                  startDate: '',
                  endDate: '',
                  current: false,
                  location: '',
                  description: ''
                })}
                variant="outline"
                size="sm"
              >
                <Icon name="add" size={20} className="mr-1" />
                Add Experience
              </Button>
            </div>

            {formData.workExperience.map((exp, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg relative">
                {formData.workExperience.length > 1 && (
                  <button
                    onClick={() => removeArrayItem('workExperience', index)}
                    className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                  >
                    <Icon name="delete" size={20} />
                  </button>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Company *
                    </label>
                    <input
                      type="text"
                      value={exp.company}
                      onChange={(e) => handleArrayFieldChange('workExperience', index, 'company', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Position *
                    </label>
                    <input
                      type="text"
                      value={exp.position}
                      onChange={(e) => handleArrayFieldChange('workExperience', index, 'position', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={exp.location}
                      onChange={(e) => handleArrayFieldChange('workExperience', index, 'location', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Start Date
                    </label>
                    <input
                      type="month"
                      value={exp.startDate}
                      onChange={(e) => handleArrayFieldChange('workExperience', index, 'startDate', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      End Date
                    </label>
                    <input
                      type="month"
                      value={exp.endDate}
                      onChange={(e) => handleArrayFieldChange('workExperience', index, 'endDate', e.target.value)}
                      disabled={exp.current}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={exp.current}
                        onChange={(e) => {
                          handleArrayFieldChange('workExperience', index, 'current', e.target.checked);
                          if (e.target.checked) {
                            handleArrayFieldChange('workExperience', index, 'endDate', '');
                          }
                        }}
                        className="w-4 h-4 text-primary rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">I currently work here</span>
                    </label>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={exp.description}
                      onChange={(e) => handleArrayFieldChange('workExperience', index, 'description', e.target.value)}
                      rows={3}
                      placeholder="Describe your responsibilities and achievements..."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 4: // Projects
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Projects
              </h2>
              <Button
                onClick={() => addArrayItem('projects', {
                  title: '',
                  description: '',
                  technologies: '',
                  link: '',
                  startDate: '',
                  endDate: ''
                })}
                variant="outline"
                size="sm"
              >
                <Icon name="add" size={20} className="mr-1" />
                Add Project
              </Button>
            </div>

            {formData.projects.map((project, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg relative">
                {formData.projects.length > 1 && (
                  <button
                    onClick={() => removeArrayItem('projects', index)}
                    className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                  >
                    <Icon name="delete" size={20} />
                  </button>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Project Title *
                    </label>
                    <input
                      type="text"
                      value={project.title}
                      onChange={(e) => handleArrayFieldChange('projects', index, 'title', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={project.description}
                      onChange={(e) => handleArrayFieldChange('projects', index, 'description', e.target.value)}
                      rows={3}
                      placeholder="Describe what the project does..."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Technologies Used
                    </label>
                    <input
                      type="text"
                      value={project.technologies}
                      onChange={(e) => handleArrayFieldChange('projects', index, 'technologies', e.target.value)}
                      placeholder="e.g., React, Node.js, MongoDB"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Project Link
                    </label>
                    <input
                      type="url"
                      value={project.link}
                      onChange={(e) => handleArrayFieldChange('projects', index, 'link', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Start Date
                    </label>
                    <input
                      type="month"
                      value={project.startDate}
                      onChange={(e) => handleArrayFieldChange('projects', index, 'startDate', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      End Date
                    </label>
                    <input
                      type="month"
                      value={project.endDate}
                      onChange={(e) => handleArrayFieldChange('projects', index, 'endDate', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 5: // Skills & Languages
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Skills, Languages & Links
            </h2>

            <SearchableMultiSelect
              label="Skills"
              required={true}
              value={formData.skills}
              onChange={(selected) => handleInputChange('skills', selected)}
              placeholder="Search and select skills..."
              groupedOptions={[
                {
                  label: "Programming Languages",
                  options: ["JavaScript", "TypeScript", "Python", "Java", "C++", "C#", "Go", "Rust", "PHP", "Ruby", "Swift", "Kotlin", "R", "MATLAB"]
                },
                {
                  label: "Frontend",
                  options: ["React", "Angular", "Vue.js", "HTML", "CSS", "SASS", "Tailwind CSS", "Bootstrap", "jQuery"]
                },
                {
                  label: "Backend",
                  options: ["Node.js", "Express.js", "Django", "Flask", "Spring Boot", "Laravel", "Ruby on Rails", "ASP.NET"]
                },
                {
                  label: "Databases",
                  options: ["SQL", "MySQL", "PostgreSQL", "MongoDB", "Redis", "Oracle", "SQLite", "Cassandra"]
                },
                {
                  label: "Cloud & DevOps",
                  options: ["AWS", "Azure", "Google Cloud", "Docker", "Kubernetes", "Jenkins", "CI/CD", "Terraform"]
                },
                {
                  label: "Data Science & AI",
                  options: ["Machine Learning", "Deep Learning", "TensorFlow", "PyTorch", "Pandas", "NumPy", "Scikit-learn", "Data Analysis"]
                },
                {
                  label: "Other",
                  options: ["Git", "GitHub", "REST API", "GraphQL", "Agile", "Scrum", "Project Management", "UI/UX Design"]
                }
              ]}
            />

            <SearchableMultiSelect
              label="Languages"
              required={true}
              value={formData.languages}
              onChange={(selected) => handleInputChange('languages', selected)}
              placeholder="Search and select languages..."
              options={["English", "Hindi", "Tamil", "Telugu", "Kannada", "Malayalam", "Bengali", "Marathi", "Gujarati", "Punjabi", "Spanish", "French", "German", "Chinese", "Japanese", "Korean", "Arabic", "Russian"]}
            />

            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Social Links (Optional)
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    LinkedIn Profile
                  </label>
                  <input
                    type="url"
                    value={formData.linkedin}
                    onChange={(e) => handleInputChange('linkedin', e.target.value)}
                    placeholder="https://linkedin.com/in/your-profile"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    GitHub Profile
                  </label>
                  <input
                    type="url"
                    value={formData.github}
                    onChange={(e) => handleInputChange('github', e.target.value)}
                    placeholder="https://github.com/your-username"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Portfolio Website
                  </label>
                  <input
                    type="url"
                    value={formData.portfolio}
                    onChange={(e) => handleInputChange('portfolio', e.target.value)}
                    placeholder="https://your-portfolio.com"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 6: // Courses
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Courses & Certifications
              </h2>
              <Button
                onClick={() => addArrayItem('courses', {
                  name: '',
                  provider: '',
                  completionDate: '',
                  certificateUrl: ''
                })}
                variant="outline"
                size="sm"
              >
                <Icon name="add" size={20} className="mr-1" />
                Add Course
              </Button>
            </div>

            {formData.courses.map((course, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg relative">
                {formData.courses.length > 1 && (
                  <button
                    onClick={() => removeArrayItem('courses', index)}
                    className="absolute top-4 right-4 text-red-500 hover:text-red-700"
                  >
                    <Icon name="delete" size={20} />
                  </button>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Course Name *
                    </label>
                    <input
                      type="text"
                      value={course.name}
                      onChange={(e) => handleArrayFieldChange('courses', index, 'name', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Provider
                    </label>
                    <input
                      type="text"
                      value={course.provider}
                      onChange={(e) => handleArrayFieldChange('courses', index, 'provider', e.target.value)}
                      placeholder="e.g., Coursera, Udemy"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Completion Date
                    </label>
                    <input
                      type="month"
                      value={course.completionDate}
                      onChange={(e) => handleArrayFieldChange('courses', index, 'completionDate', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Certificate URL
                    </label>
                    <input
                      type="url"
                      value={course.certificateUrl}
                      onChange={(e) => handleArrayFieldChange('courses', index, 'certificateUrl', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'resume': // Resume Upload (for manual mode before preview)
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Upload Your Resume (Optional)
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                Upload your resume document. This is optional - you can skip if you prefer.
              </p>
            </div>

            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-12 text-center hover:border-primary transition-colors">
              <input
                type="file"
                id="resume-upload-manual"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      alert('File size must be less than 5MB');
                      return;
                    }
                    setResumeFile(file);
                  }
                }}
                className="hidden"
              />
              <label htmlFor="resume-upload-manual" className="cursor-pointer">
                <Icon name="upload_file" size={64} className="text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {resumeFile ? resumeFile.name : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  PDF, DOC, or DOCX (max 5MB)
                </p>
              </label>
            </div>

            {resumeFile && (
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-200">
                  <Icon name="check_circle" size={16} className="inline mr-1" />
                  Resume uploaded: {resumeFile.name}
                </p>
              </div>
            )}

            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <Icon name="info" size={16} className="inline mr-1" />
                You can skip this step if you don't have a resume ready. Your profile details are sufficient for job applications.
              </p>
            </div>
          </div>
        );

      case 7: // Preview
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Preview Your Profile
            </h2>

            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Basic Information</h3>
                <p className="text-gray-700 dark:text-gray-300">{formData.fullName}</p>
                {formData.jobTitle && <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">{formData.jobTitle}</p>}
                <p className="text-gray-600 dark:text-gray-400 text-sm">{formData.email} • {formData.phone}</p>
                {formData.location && <p className="text-gray-600 dark:text-gray-400 text-sm">{formData.location}</p>}
              </div>

              {formData.education.length > 0 && formData.education[0].institution && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Education</h3>
                  {formData.education.map((edu, i) => (
                    <div key={i} className="mb-2">
                      <p className="text-gray-700 dark:text-gray-300">{edu.degree} - {edu.institution}</p>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">{edu.field}</p>
                    </div>
                  ))}
                </div>
              )}

              {formData.workExperience.length > 0 && formData.workExperience[0].company && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Work Experience</h3>
                  {formData.workExperience.map((exp, i) => (
                    <div key={i} className="mb-2">
                      <p className="text-gray-700 dark:text-gray-300">{exp.position} at {exp.company}</p>
                      <p className="text-gray-600 dark:text-gray-400 text-sm">{exp.startDate} - {exp.current ? 'Present' : exp.endDate}</p>
                    </div>
                  ))}
                </div>
              )}

              {formData.skills.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill, i) => (
                      <span key={i} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {resumeFile && (
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Resume</h3>
                  <p className="text-gray-700 dark:text-gray-300">{resumeFile.name}</p>
                </div>
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <Icon name="info" size={16} className="inline mr-1" />
                Review your information carefully. You can always update your profile later.
              </p>
            </div>
          </div>
        );

      // Continue with other steps...
      default:
        return <div>Step {currentStep}</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex flex-col items-center ${index <= currentStep ? 'text-primary' : 'text-gray-400'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    index <= currentStep ? 'bg-primary text-white' : 'bg-gray-200 dark:bg-gray-700'
                  }`}>
                    <Icon name={step.icon} size={20} />
                  </div>
                  <span className="text-xs mt-1 hidden md:block">{step.title}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`h-0.5 w-12 mx-2 ${index < currentStep ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg">
          {/* Debug info - remove after testing */}
          <div className="text-xs text-gray-500 mb-2">
            Current Step: {currentStep} | Total Steps: {steps.length} | Mode: {mode}
          </div>
          
          {renderStepContent()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              onClick={handleBack}
              variant="outline"
              disabled={currentStep === 0}
            >
              <Icon name="arrow_back" size={20} className="mr-2" />
              Back
            </Button>

            {currentStep < steps.length - 1 ? (
              <Button onClick={handleNext} variant="primary">
                Next
                <Icon name="arrow_forward" size={20} className="ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Profile'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Profile Completion Success Modal */}
      <ProfileCompletionModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/'); // Redirect to home page where verification banner and popup will show
        }}
        creditsEarned={100}
      />
    </div>
  );
};

export default ProfileSetupPage;

